import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area,
} from 'recharts';

interface ChartsSectionProps {
  epiBySector: { name: string; count: number }[];
  epiByStatus: { name: string; value: number; color: string }[];
  epiByType: { name: string; count: number }[];
  monthlyData: { name: string; epi: number; incidentes: number; denuncias: number }[];
  trendData: { name: string; epi: number; incidentes: number; denuncias: number }[];
  incidentsByType: { name: string; count: number }[];
  denunciationsByLevel: { name: string; count: number }[];
  denunciationsBySector: { name: string; count: number }[];
}

const COLORS = ['#5B3DF5', '#7B61FF', '#A78BFA', '#C4B5FD', '#DDD6FE', '#EDE9FE'];
const INCIDENT_COLORS = ['#F59E0B', '#EF4444', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#14B8A6', '#6366F1', '#84CC16', '#6B7280'];
const LEVEL_COLORS = ['#10B981', '#F59E0B', '#EF4444'];

function CustomTooltip({ active, payload, label }: Record<string, unknown>) {
  if (!active || !payload || !Array.isArray(payload)) return null;
  return (
    <div className="bg-white border border-portal-gray-200 rounded-lg px-3 py-2 text-xs">
      <p className="font-medium text-portal-gray-700 mb-1">{label as string}</p>
      {(payload as Array<{ name: string; value: number; color: string }>).map((entry, i) => (
        <p key={i} className="text-portal-gray-600">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: entry.color }}></span>
          {entry.name}: <span className="font-medium">{entry.value}</span>
        </p>
      ))}
    </div>
  );
}

export default function ChartsSection({
  epiBySector,
  epiByStatus,
  epiByType,
  monthlyData,
  trendData,
  incidentsByType,
  denunciationsByLevel,
  denunciationsBySector,
}: ChartsSectionProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      {/* Solicitações por Setor */}
      <div className="bg-white rounded-xl border border-portal-gray-200 p-5">
        <h3 className="text-sm font-semibold text-portal-gray-800 mb-4">Solicitações de EPI por Setor</h3>
        {epiBySector.length === 0 ?(
          <p className="text-sm text-portal-gray-400 py-8 text-center">Nenhum dado no período selecionado.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={epiBySector} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F4F1FE' }} />
              <Bar dataKey="count" name="Solicitações" radius={[6, 6, 0, 0]} fill="#5B3DF5" maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Status das Solicitações */}
      <div className="bg-white rounded-xl border border-portal-gray-200 p-5">
        <h3 className="text-sm font-semibold text-portal-gray-800 mb-4">Status das Solicitações</h3>
        {epiByStatus.reduce((sum, s) => sum + s.value, 0) === 0 ?(
          <p className="text-sm text-portal-gray-400 py-8 text-center">Nenhum dado no período selecionado.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={epiByStatus}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={4}
                dataKey="value"
                stroke="none"
              >
                {epiByStatus.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="bottom"
                iconType="circle"
                iconSize={8}
                formatter={(value: string) => <span className="text-xs text-portal-gray-600">{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Evolução Mensal - Barra */}
      <div className="bg-white rounded-xl border border-portal-gray-200 p-5">
        <h3 className="text-sm font-semibold text-portal-gray-800 mb-4">Evolução Mensal</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={monthlyData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F4F1FE' }} />
            <Bar dataKey="epi" name="EPIs" radius={[4, 4, 0, 0]} fill="#5B3DF5" maxBarSize={20} />
            <Bar dataKey="incidentes" name="Incidentes" radius={[4, 4, 0, 0]} fill="#F59E0B" maxBarSize={20} />
            <Bar dataKey="denuncias" name="Denúncias" radius={[4, 4, 0, 0]} fill="#EF4444" maxBarSize={20} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tendência Acumulada - Área */}
      <div className="bg-white rounded-xl border border-portal-gray-200 p-5">
        <h3 className="text-sm font-semibold text-portal-gray-800 mb-4">Tendência Acumulada</h3>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={trendData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
            <defs>
              <linearGradient id="colorEpi" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#5B3DF5" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#5B3DF5" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorInc" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorDen" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="epi" name="EPIs" stroke="#5B3DF5" fill="url(#colorEpi)" strokeWidth={2} />
            <Area type="monotone" dataKey="incidentes" name="Incidentes" stroke="#F59E0B" fill="url(#colorInc)" strokeWidth={2} />
            <Area type="monotone" dataKey="denuncias" name="Denúncias" stroke="#EF4444" fill="url(#colorDen)" strokeWidth={2} />
            <Legend iconType="circle" iconSize={8} formatter={(value: string) => <span className="text-xs text-portal-gray-600">{value}</span>} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Incidentes por Tipo */}
      <div className="bg-white rounded-xl border border-portal-gray-200 p-5">
        <h3 className="text-sm font-semibold text-portal-gray-800 mb-4">Incidentes por Tipo</h3>
        {incidentsByType.length === 0 ?(
          <p className="text-sm text-portal-gray-400 py-8 text-center">Nenhum dado no período selecionado.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={incidentsByType} layout="vertical" margin={{ top: 5, right: 5, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#6B7280' }} width={140} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F4F1FE' }} />
              <Bar dataKey="count" name="Incidentes" radius={[0, 6, 6, 0]} maxBarSize={24}>
                {incidentsByType.map((_, i) => (
                  <Cell key={i} fill={INCIDENT_COLORS[i % INCIDENT_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Denúncias por Nível de Gravidade */}
      <div className="bg-white rounded-xl border border-portal-gray-200 p-5">
        <h3 className="text-sm font-semibold text-portal-gray-800 mb-4">Denúncias por Nível de Gravidade</h3>
        {denunciationsByLevel.reduce((sum, d) => sum + d.count, 0) === 0 ?(
          <p className="text-sm text-portal-gray-400 py-8 text-center">Nenhum dado no período selecionado.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={denunciationsByLevel} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F4F1FE' }} />
              <Bar dataKey="count" name="Denúncias" radius={[6, 6, 0, 0]} maxBarSize={48}>
                {denunciationsByLevel.map((_, i) => (
                  <Cell key={i} fill={LEVEL_COLORS[i % LEVEL_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Denúncias por Setor */}
      <div className="bg-white rounded-xl border border-portal-gray-200 p-5">
        <h3 className="text-sm font-semibold text-portal-gray-800 mb-4">Denúncias por Setor</h3>
        {denunciationsBySector.length === 0 ?(
          <p className="text-sm text-portal-gray-400 py-8 text-center">Nenhum dado no período selecionado.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={denunciationsBySector}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={4}
                dataKey="count"
                stroke="none"
              >
                {denunciationsBySector.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="bottom"
                iconType="circle"
                iconSize={8}
                formatter={(value: string) => <span className="text-xs text-portal-gray-600">{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* EPIs Mais Solicitados */}
      <div className="bg-white rounded-xl border border-portal-gray-200 p-5">
        <h3 className="text-sm font-semibold text-portal-gray-800 mb-4">EPIs Mais Solicitados</h3>
        {epiByType.length === 0 ?(
          <p className="text-sm text-portal-gray-400 py-8 text-center">Nenhum dado no período selecionado.</p>
        ) : (
          <div className="space-y-3">
            {epiByType.map((item, i) => {
              const max = epiByType[0].count;
              const pct = max > 0 ?(item.count / max) * 100 : 0;
              return (
                <div key={item.name} className="flex items-center gap-3">
                  <span className="text-xs font-medium text-portal-gray-600 w-24 whitespace-nowrap truncate">{item.name}</span>
                  <div className="flex-1 h-5 bg-portal-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }}
                    ></div>
                  </div>
                  <span className="text-xs font-semibold text-portal-gray-700 w-6 text-right">{item.count}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}